import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  CalendarToday,
  AccessTime,
  Person,
  LocationOn,
  Refresh,
  Download
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  searchAppointments, 
  setSearchFilters, 
  clearSearchResults,
  resetSearchState 
} from '../features/appointments/appointmentSearchSlice';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

const AppointmentSearch = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { 
    searchResults, 
    searchFilters, 
    pagination, 
    loading, 
    error 
  } = useSelector(state => state.appointmentSearch);

  const [localFilters, setLocalFilters] = useState({
    q: '',
    status: '',
    date_from: '',
    date_to: '',
    doctor_id: '',
    patient_id: ''
  });

  useEffect(() => {
    // Load initial search with empty filters to show all appointments
    dispatch(searchAppointments({ page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    dispatch(setSearchFilters(localFilters));
    dispatch(searchAppointments({ ...localFilters, page: 1 }));
  };

  const handleClearFilters = () => {
    setLocalFilters({
      q: '',
      status: '',
      date_from: '',
      date_to: '',
      doctor_id: '',
      patient_id: ''
    });
    dispatch(clearSearchResults());
  };

  const handlePageChange = (event, page) => {
    const newFilters = { ...localFilters, page };
    dispatch(setSearchFilters(newFilters));
    dispatch(searchAppointments(newFilters));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'Pending': return 'warning';
      case 'Canceled': return 'error';
      case 'Confirmed': return 'info';
      case 'Scheduled': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          🔍 Appointment Search
        </Typography>

        {/* Search Filters */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
            Search Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Query"
                placeholder="Search by patient, doctor, or clinic name..."
                value={localFilters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Confirmed">Confirmed</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Canceled">Canceled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={localFilters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={localFilters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Doctor ID"
                placeholder="Enter doctor ID"
                value={localFilters.doctor_id}
                onChange={(e) => handleFilterChange('doctor_id', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Patient ID"
                placeholder="Enter patient ID"
                value={localFilters.patient_id}
                onChange={(e) => handleFilterChange('patient_id', e.target.value)}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
              disabled={loading}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<Clear />}
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              onClick={() => dispatch(resetSearchState())}
              startIcon={<Refresh />}
            >
              Reset
            </Button>
          </Box>
        </Card>

        {/* Search Results */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {typeof error === 'string' ? error : error?.detail || error?.message || 'An error occurred'}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && searchResults.length === 0 && pagination.total_count === 0 && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary">
                No appointments found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Object.values(localFilters).some(v => v) 
                  ? 'Try adjusting your search filters or check if appointments exist in the system'
                  : 'No appointments available. Create some appointments to see them here.'}
              </Typography>
            </CardContent>
          </Card>
        )}

        {searchResults.length > 0 && (
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Search Results ({pagination.total_count} appointments)
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  size="small"
                >
                  Export
                </Button>
              </Box>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Patient</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Clinic</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((appointment) => (
                      <TableRow key={appointment.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Person sx={{ mr: 1, color: 'text.secondary' }} />
                            {appointment.patient?.name || appointment.patient_name || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {appointment.doctor?.user?.name || appointment.doctor?.name || appointment.doctor_name || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box display="flex" alignItems="center">
                              <CalendarToday sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                              {appointment.scheduled_date ? 
                                (typeof appointment.scheduled_date === 'string' ? 
                                  appointment.scheduled_date.split('T')[0] : 
                                  appointment.scheduled_date) : 
                                'N/A'}
                            </Box>
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <AccessTime sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                              {appointment.scheduled_time || 'N/A'}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={appointment.status || 'Unknown'}
                            color={getStatusColor(appointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {appointment.type || appointment.consultation_type || 'Consultation'}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <LocationOn sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                            {appointment.clinic?.name || appointment.clinic_name || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined">
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {pagination.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={pagination.total_pages}
                    page={pagination.current_page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

      </motion.div>
    </Container>
  );
};

export default AppointmentSearch;